/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 * 
 * NOTE: Once the PageProvider is implemented, the functionality of this provider should probably be 
 * moved to there.  Once done, the need for the React.portal will no longer be required as well.
 * 
 */
import React, { useContext, useReducer } from 'react';
import { WindowProvider } from './window';
import { AuthenticationProvider } from './authentication';
import { MarketProvider } from './market';
import { ModalContainer, useModals } from '../components/modals';

const PageStateContext = React.createContext();
const PageDispatchContext = React.createContext();

/*******************************/
/** Site                      **/
/*******************************/
const siteRegex = /^[A-Z]{2}\/[a-z]{2}$/g;

const parseWindowLocation = () => {
    let index = window.location.href.indexOf(window.location.pathname);
    const baseUrl = window.location.href.substring(0, index);
    let contextPath = baseUrl;
    let path = window.location.pathname;
    let queryParams = window.location.search;
    let context = path.substring(1, 6);
    let site = null;
    let language = null;
    if (siteRegex.test(context)) {
        path = path.substring(7);
        contextPath = `${baseUrl}/${context}`;
        site = context.substring(0, 2);
        language = context.substring(3);
    } else {
        context = null;
    }
    return {baseUrl, contextPath, context, site, language, path, queryParams};
};


/*******************************/
/** Page provider             **/
/*******************************/

const SET_LANGUAGE = 'SET_LANGUAGE';

const PageReducer = (state, action) => {
    switch (action.type) {
        case SET_LANGUAGE: {
            if (state.language !== action.payload) {
                return {...state, language: action.payload};
            }
            return state;
        }
        default: {
            throw new Error(`[PageReducer] Unsupported action type: ${action.type}.`);
        }
    }
};


export const PageProvider = (props) => {
    const { children } = props;
    const site = parseWindowLocation();
    const { modals, showModal, hideModal } = useModals();
    const [state, dispatch] = useReducer(PageReducer, {site, language: site.language, showModal, hideModal});

    const hide = () => {
        hideModal();
    };

    return (
        <WindowProvider>
            <PageStateContext.Provider value={state}> 
                <PageDispatchContext.Provider value={dispatch}>
                    <AuthenticationProvider>
                        <MarketProvider>
                            {children}
                        </MarketProvider>
                    </AuthenticationProvider>
                    <ModalContainer modals={modals} close={hide} overlayIndex='1070' wrapperIndex='1071'/>
                </PageDispatchContext.Provider>
            </PageStateContext.Provider>
        </WindowProvider>
    );

};


/*******************************/
/** Page hooks                **/
/*******************************/

export const useSiteState = () => {
    const context = useContext(PageStateContext);
    if (!context) {
        throw new Error('useSiteState must be used within a PageProvider');
    }
    return {...context.site};
};

export const useSiteMutator = () => {
    const dispatch = useContext(PageDispatchContext);
    if (!dispatch) {
        throw new Error('useSiteMutator must be used within a PageProvider');
    }
    const setLanguage = (language) => {
        dispatch({type: SET_LANGUAGE, payload: language});
    };
    return { setLanguage };
};

export const useModalsMutator = () => {
    const dispatch = useContext(PageDispatchContext);
    if (!dispatch) {
        throw new Error('usePageMutator must be used within a PageProvider');
    }
    const { showModal, hideModal } = useContext(PageStateContext);
    return { showGlobalModal: showModal, hideGlobalModal: hideModal };
};
